import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Typography,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  Box,
  Stack,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { LogOut, Calendar, Mail, User } from 'lucide-react';

const Profile = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    if (!token) {
      console.error("No token found! Redirecting to login...");
      navigate("/login");
      return;
    }
    axios
      .get("https://shopify-inv-tracker-backend-d0b1c1125141.herokuapp.com/api/auth/profile", {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        withCredentials: true,
      })
      .then((res) => { setUser(res.data); setLoading(false); })
      .catch((error) => {
        console.error("Authentication failed:", error.response?.data);
        navigate("/login");
      });
  }, [navigate]);

  const handleLogout = async () => {
    try {
      await axios.post("https://shopify-inv-tracker-backend-d0b1c1125141.herokuapp.com/api/auth/logout", {}, { withCredentials: true });
      localStorage.removeItem("token");
      sessionStorage.removeItem("token");
      setUser(null);
      setSnackbar({ open: true, message: "Logged out successfully", severity: "success" });
      setTimeout(() => navigate("/login"), 1000);
      window.location.reload();
    } catch (error) {
      console.error("Logout failed:", error.response?.data);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric", month: "long", day: "numeric",
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" sx={{ height: 300 }}>
        <CircularProgress size={32} thickness={3} />
      </Box>
    );
  }

  return (
    <Box className="fade-in" sx={{ maxWidth: 480, mx: 'auto', mt: 6 }}>
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        {/* Avatar */}
        <Box
          sx={{
            width: 72,
            height: 72,
            borderRadius: '50%',
            bgcolor: '#2383e2',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontSize: '1.75rem',
            fontWeight: 700,
            mb: 2,
          }}
        >
          {user?.name?.charAt(0).toUpperCase()}
        </Box>

        <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: '-0.02em' }}>
          {user?.name}
        </Typography>
      </Box>

      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2.5, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Mail size={16} style={{ opacity: 0.5 }} />
          <Box>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, display: 'block', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Email</Typography>
            <Typography variant="body2">{user?.email}</Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2.5, py: 2 }}>
          <Calendar size={16} style={{ opacity: 0.5 }} />
          <Box>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, display: 'block', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Joined</Typography>
            <Typography variant="body2">{formatDate(user?.createdAt)}</Typography>
          </Box>
        </Box>
      </Box>

      <Button
        fullWidth
        variant="outlined"
        color="error"
        onClick={handleLogout}
        startIcon={<LogOut size={16} />}
        sx={{ mt: 3, py: 1.25, borderRadius: 2 }}
      >
        Sign out
      </Button>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default Profile;
